import { useState, useRef, useEffect, ReactNode } from 'react';
import { useLazyDocumentPreview } from '../../api/bewerber';
import { Dokument, isDocumentPreviewable, isImageMimeType, isPdfMimeType } from '../../types/bewerber';
import { PdfThumbnail } from './PdfThumbnail';

interface DocumentPreviewTooltipProps {
  document: Dokument;
  bewerberId: string;
  children: ReactNode;
}

export function DocumentPreviewTooltip({
  document,
  bewerberId,
  children,
}: DocumentPreviewTooltipProps) {
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { fetchPreview } = useLazyDocumentPreview();

  const isPreviewable = isDocumentPreviewable(document.mimeType);
  const isImage = isImageMimeType(document.mimeType);
  const isPdf = isPdfMimeType(document.mimeType);

  const loadPreview = async () => {
    if (previewUrl) return; // Already loaded

    setIsLoading(true);
    setError(null);

    try {
      const url = await fetchPreview(bewerberId, document.id);
      setPreviewUrl(url);
    } catch (err) {
      setError('Vorschau konnte nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  };

  const calculatePosition = () => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const tooltipWidth = 220;
    const tooltipHeight = isImage ? 180 : 160;
    const padding = 10;

    let top = rect.top - tooltipHeight - padding;
    let left = rect.left + rect.width / 2 - tooltipWidth / 2;

    // Adjust if tooltip would go above viewport
    if (top < padding) {
      top = rect.bottom + padding;
    }

    // Adjust if tooltip would go off left edge
    if (left < padding) {
      left = padding;
    }

    // Adjust if tooltip would go off right edge
    if (left + tooltipWidth > window.innerWidth - padding) {
      left = window.innerWidth - tooltipWidth - padding;
    }

    setPosition({ top, left });
  };

  const handleMouseEnter = () => {
    if (!isPreviewable) return;

    hoverTimeoutRef.current = setTimeout(() => {
      calculatePosition();
      setShowPreview(true);
      loadPreview();
    }, 500); // 500ms debounce
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setShowPreview(false);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="relative inline-block"
    >
      {children}

      {/* Tooltip preview */}
      {showPreview && (
        <div
          className="fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden"
          style={{
            top: position.top,
            left: position.left,
            width: 220,
          }}
        >
          {isLoading ? (
            <div className="flex items-center justify-center h-32 bg-gray-50">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-32 bg-gray-50">
              <p className="text-xs text-gray-500">{error}</p>
            </div>
          ) : previewUrl ? (
            <div className="bg-gray-100">
              {isImage && (
                <img
                  src={previewUrl}
                  alt={document.originalFilename}
                  className="w-full h-40 object-contain"
                />
              )}
              {isPdf && (
                <PdfThumbnail
                  url={previewUrl}
                  width={220}
                  height={160}
                />
              )}
            </div>
          ) : null}

          {/* Filename at bottom */}
          <div className="px-2 py-1.5 bg-gray-50 border-t border-gray-200">
            <p className="text-xs text-gray-700 truncate font-medium">
              {document.originalFilename}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
